import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useDuelRealtime } from '../../../hooks/useDuelRealtime';
import { duelService } from '../../../services/socialService';

const DuelCard = ({ duel: initialDuel, userId, onClick, index }) => {
  const [duel, setDuel] = useState(initialDuel);

  // Update local state when prop changes
  useEffect(() => {
    setDuel(initialDuel);
  }, [initialDuel]);

  // Subscribe to real-time updates for this duel
  useDuelRealtime(duel?.id, userId, {
    onDuelUpdate: (payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new) {
        setDuel(prev => ({ ...prev, ...payload?.new }));
      }
    },
    onProofUpload: async (payload) => {
      // Refetch duel to get updated progress
      try {
        const { data } = await duelService?.getUserDuels(userId);
        const updatedDuel = data?.find(d => d?.id === duel?.id);
        if (updatedDuel) {
          setDuel(updatedDuel);
        }
      } catch (error) {
        console.error('Error refetching duel:', error);
      }
    }
  });

  const isCreator = duel?.creator_id === userId;
  const opponent = isCreator ? duel?.opponent : duel?.creator;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
  
  const progressPercentage = Math?.min((userProgress / duel?.target_value) * 100, 100);
  const opponentProgressPercentage = Math?.min((opponentProgress / duel?.target_value) * 100, 100);
  
  const statusConfig = {
    pending: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent', label: 'Pendiente' },
    active: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', label: 'Activo' },
    completed: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', label: 'Completado' },
    cancelled: { bg: 'bg-muted/30', border: 'border-muted', text: 'text-muted-foreground', label: 'Cancelado' }
  };

  const status = statusConfig?.[duel?.status] || statusConfig?.pending;
  
  const metricIcons = {
    gym_workouts: 'Dumbbell',
    calories_limit: 'Flame',
    sleep_hours: 'Moon',
    water_intake: 'Droplets',
    daily_steps: 'Footprints',
    daily_minimum: 'CheckCircle'
  };

  const daysLeft = duel?.end_date ? Math?.ceil((new Date(duel?.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const isWinner = duel?.winner_id === userId;
  const isLoser = duel?.winner_id && duel?.winner_id !== userId;

  // Streak counter (days with proofs)
  const streakDays = duel?.creator_progress || 0;

  // Real-time status indicator
  const getRealtimeStatus = () => {
    if (duel?.verification_mode !== 'realtime') return null;
    return (
      <div className="flex items-center space-x-1 text-xs">
        <div className="w-2 h-2 rounded-full bg-error animate-pulse" />
        <span className="text-error font-medium">Modo Real</span>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onClick={onClick}
      className={`relative p-6 rounded-xl border-2 ${status?.bg} ${status?.border} hover:shadow-lg transition-all duration-300 cursor-pointer`}
    >
      {/* Status Badge */}
      <div className="absolute top-4 right-4 flex flex-col items-end space-y-2">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status?.bg} ${status?.text}`}>
          {status?.label}
        </span>
        {getRealtimeStatus()}
      </div>
      {/* Duel Icon */}
      <div className={`w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4`}>
        <Icon name={metricIcons?.[duel?.metric] || 'Target'} size={24} className="text-primary" />
      </div>
      {/* Duel Info */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">{duel?.title}</h3>
        <p className="text-sm text-muted-foreground mb-3">{duel?.description}</p>
        
        {/* Opponent */}
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="User" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            vs {opponent?.full_name}
          </span>
        </div>

        {/* Time Left + Streak */}
        <div className="flex items-center justify-between">
          {duel?.status === 'active' && daysLeft !== null && (
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {daysLeft > 0 ? `${daysLeft} días restantes` : 'Último día'}
              </span>
            </div>
          )}
          {duel?.status === 'active' && streakDays > 0 && (
            <div className="flex items-center space-x-1">
              <Icon name="Flame" size={16} className="text-warning" />
              <span className="text-sm font-bold text-warning">{streakDays}</span>
              <span className="text-xs text-muted-foreground">racha</span>
            </div>
          )}
        </div>
      </div>
      {/* Progress with Micro-animations */}
      {duel?.status === 'active' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tu Progreso</span>
              <motion.span 
                className="font-medium text-primary"
                key={userProgress}
                initial={{ scale: 1.3, color: '#10b981' }}
                animate={{ scale: 1, color: 'currentColor' }}
                transition={{ duration: 0.3 }}
              >
                {userProgress} / {duel?.target_value}
              </motion.span>
            </div>
            <div className="w-full bg-muted/50 rounded-full h-2 relative overflow-hidden">
              <motion.div
                className="h-2 rounded-full bg-primary relative"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
              >
                {/* Shine effect on progress update */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '200%' }}
                  transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                />
              </motion.div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{opponent?.full_name}</span>
              <span className="font-medium text-muted-foreground">
                {opponentProgress} / {duel?.target_value}
              </span>
            </div>
            <div className="w-full bg-muted/50 rounded-full h-2">
              <motion.div
                className="h-2 rounded-full bg-muted-foreground"
                initial={{ width: 0 }}
                animate={{ width: `${opponentProgressPercentage}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 + 0.4 }}
              />
            </div>
          </div>

          {/* Winning/Losing Indicator */}
          {userProgress !== opponentProgress && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.6 }}
              className={`text-center py-2 rounded-lg ${
                userProgress > opponentProgress 
                  ? 'bg-success/10 text-success' :'bg-error/10 text-error'
              }`}
            >
              <span className="text-xs font-bold">
                {userProgress > opponentProgress ? '¡Vas Ganando! 🔥' : 'Vas Perdiendo 💪'}
              </span>
            </motion.div>
          )}
        </div>
      )}
      {/* Completed Result */}
      {duel?.status === 'completed' && (
        <div className={`p-3 rounded-lg ${isWinner ? 'bg-success/10' : 'bg-error/10'}`}>
          <div className="flex items-center space-x-2">
            <Icon name={isWinner ? 'Trophy' : 'X'} size={20} className={isWinner ? 'text-success' : 'text-error'} />
            <span className={`text-sm font-medium ${isWinner ? 'text-success' : 'text-error'}`}>
              {isWinner ? '¡Ganaste!' : 'Perdiste - Paga el stake'}
            </span>
          </div>
        </div>
      )}
      {/* Stake */}
      {duel?.stake && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name={duel?.stake?.icon} size={16} className="text-warning" />
            <span className="text-sm font-medium text-foreground">
              Stake: {duel?.stake?.title}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default DuelCard;
